import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HLControlPanel } from './ops/HLControlPanel';
import { AdvisorChat } from './ops/AdvisorChat';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  Activity, AlertTriangle, RefreshCw, Bot, TrendingUp,
  ShieldCheck, ArrowUpRight, ArrowDownRight, CheckCircle2,
  XCircle, Clock, Cpu, Sparkles, FileText, Send, BarChart3, OctagonX, ScrollText, Radio, Database, Brain, Scissors,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  API_URL, ADMIN_EMAILS, BOT_LABELS, BOT_COLORS,
  formatUSD, formatUSDCompact, formatPct, formatTs, authHeaders, apiGet,
} from './ops/ops_helpers';
import { BotAnalytics } from './ops/BotAnalytics';
import { BotComparison } from './ops/BotComparison';
import { BotSignals } from './ops/BotSignals';
import { BotConfigEditor } from './ops/BotConfigEditor';
import { RiskControlPanel } from './ops/RiskControlPanel';
import { EmergencyStopPanel } from './ops/EmergencyStopPanel';
import { LogsPanel } from './ops/LogsPanel';
import { FleetPanel } from './ops/FleetPanel';
import { SentimentInsights } from './ops/SentimentInsights';
import { SmartExitPanel } from './ops/SmartExitPanel';

// =============================================================================
// Mini Components reutilizados (KPI overview, bot card, markdown render)
// =============================================================================

const KpiCard = ({ icon: Icon, label, value, hint, tone = 'default', testId }) => {
  const toneClass = {
    default: 'text-zinc-100',
    success: 'text-emerald-400',
    danger: 'text-rose-400',
    warning: 'text-amber-400',
  }[tone] || 'text-zinc-100';
  return (
    <Card data-testid={testId} className="bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</p>
            <p className={`mt-2 text-2xl font-bold ${toneClass}`}>{value}</p>
            {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
          </div>
          {Icon && <Icon className={`w-5 h-5 ${toneClass} opacity-80`} />}
        </div>
      </CardContent>
    </Card>
  );
};

const BotStatusCard = ({ bot, onClick, onReset }) => {
  const isStale = (() => {
    if (typeof bot.is_stale === 'boolean') return bot.is_stale;
    if (!bot.ts) return true;
    const ageMin = (Date.now() - new Date(bot.ts).getTime()) / 60000;
    return ageMin > 3;
  })();
  const statusColor = isStale ? 'bg-zinc-700 text-zinc-400' :
    bot.status === 'running' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
    bot.status === 'paused' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
    'bg-rose-500/20 text-rose-400 border-rose-500/30';
  const filtersActive = bot.filters_active
    ? Object.entries(bot.filters_active).filter(([, v]) => v === true).length
    : 0;
  const filtersTotal = bot.filters_active ? Object.keys(bot.filters_active).length : 0;
  const dd = parseFloat((bot.server_drawdown_pct != null ? bot.server_drawdown_pct : bot.drawdown_pct) || 0);
  const ddTone = dd > 10 ? 'text-rose-400' : dd > 5 ? 'text-amber-400' : 'text-zinc-300';
  const badEquity = bot.data_quality === 'bad_equity' || bot.equity_valid === false;

  return (
    <Card
      data-testid={`bot-card-${bot.bot_id}`}
      className="bg-zinc-900/60 border-zinc-800 hover:border-violet-500/40 transition-all cursor-pointer"
      onClick={onClick}
      style={{ borderTopColor: BOT_COLORS[bot.bot_id] || '#666', borderTopWidth: 3 }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base font-semibold text-zinc-100">
            {BOT_LABELS[bot.bot_id] || bot.bot_id}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {bot.halted && (
              <Badge variant="outline" data-testid={`bot-halted-badge-${bot.bot_id}`}
                className="text-xs bg-rose-600/20 text-rose-300 border-rose-700/50">
                PARADO
              </Badge>
            )}
            {bot.dry_run && (
              <Badge variant="outline" data-testid={`bot-dryrun-badge-${bot.bot_id}`}
                className="text-xs bg-sky-600/20 text-sky-300 border-sky-700/50">
                OBSERVACIÓN · no opera
              </Badge>
            )}
            <Badge variant="outline" className={`text-xs ${statusColor}`}>
              {isStale ? 'stale' : bot.status}
            </Badge>
          </div>
        </div>
        <CardDescription className="text-xs text-zinc-500 mt-1">
          v{bot.version} · {bot.host || 'n/a'} · {formatTs(bot.ts)}
        </CardDescription>
        {badEquity && (
          <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-300"
            data-testid={`bot-data-quality-${bot.bot_id}`}>
            ⚠ Última lectura de equity inválida — mostrando el último valor bueno.
          </div>
        )}
        {bot.extra?.account_login && (
          <div
            className="mt-2 flex flex-wrap items-center gap-1.5 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-200"
            data-testid={`bot-account-badge-${bot.bot_id}`}
          >
            <span className="font-mono text-amber-400">#{bot.extra.account_login}</span>
            {bot.extra.account_server && (
              <span className="text-zinc-400">@ {bot.extra.account_server}</span>
            )}
            {bot.extra.account_size && (
              <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold text-amber-200">
                {bot.extra.account_size.toUpperCase()}
              </span>
            )}
            {bot.extra.challenge_step && (
              <span className="text-zinc-500">· {bot.extra.challenge_step}</span>
            )}
            {bot.extra.strategy_flag && (
              <span className="ml-auto rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-zinc-300">
                {bot.extra.strategy_flag}
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-zinc-500">Equity</p>
            <p className="font-bold text-zinc-100 text-base">{formatUSD(bot.equity_usd)}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">PnL hoy</p>
            <p className={`font-semibold ${(bot.pnl_today_usd || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {(bot.pnl_today_usd || 0) >= 0 ? '+' : ''}{formatUSD(bot.pnl_today_usd)}
            </p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Posiciones</p>
            <p className="font-semibold text-zinc-100">{bot.open_positions_count}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Drawdown</p>
            <p className={`font-semibold ${ddTone}`}>{formatPct(dd)}</p>
          </div>
        </div>
        <div className="pt-2 mt-1 border-t border-zinc-800 flex items-center justify-between text-xs" data-testid={`bot-db-today-${bot.bot_id}`}>
          <span className="text-zinc-500">Trades hoy (BD)</span>
          <span className="flex items-center gap-2">
            <span className={`font-semibold ${(bot.db_trades_today || 0) > 0 ? 'text-zinc-100' : 'text-amber-400'}`}>
              {bot.db_trades_today || 0}
            </span>
            {(bot.db_trades_today || 0) > 0 && (
              <span className={`${(bot.db_pnl_today_usd || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {(bot.db_pnl_today_usd || 0) >= 0 ? '+' : ''}{formatUSD(bot.db_pnl_today_usd)}
              </span>
            )}
          </span>
        </div>
        {dd > 50 && (
          <button
            data-testid={`bot-reset-peak-${bot.bot_id}`}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const r = await fetch(`${API_URL}/api/ops/bots/${bot.bot_id}/reset-peak`, { method: 'POST', headers: authHeaders() });
                if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || r.statusText);
                toast.success(`Pico de equity reseteado para ${bot.bot_id}`);
                onReset?.();
              } catch (err) { toast.error(`Error al resetear pico: ${err.message}`); }
            }}
            className="mt-2 w-full text-xs rounded border border-amber-600/50 text-amber-300 hover:bg-amber-500/10 py-1 transition-colors"
          >
            Reset pico (DD {dd.toFixed(1)}% anómalo)
          </button>
        )}
        {bot.extra?.perp_equity_usd != null && (
          <div className="pt-2 border-t border-zinc-800 grid grid-cols-3 gap-2 text-center"
            data-testid={`bot-equity-breakdown-${bot.bot_id}`}>
            <div>
              <p className="text-[10px] text-zinc-500">Spot (cash)</p>
              <p className="text-xs font-semibold text-zinc-200">{formatUSD(bot.extra.spot_usdc_usd)}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500">Perp (margen)</p>
              <p className="text-xs font-semibold text-zinc-200">{formatUSD(bot.extra.perp_equity_usd)}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500">Total</p>
              <p className="text-xs font-bold text-violet-300">{formatUSD(bot.extra.total_equity_usd)}</p>
            </div>
          </div>
        )}
        <div className="pt-2 border-t border-zinc-800 flex items-center justify-between text-xs">
          <span className="text-zinc-500 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Filtros activos
          </span>
          <span className="font-semibold text-zinc-300">{filtersActive}/{filtersTotal}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Combined equity (heartbeats) en la pestaña Resumen
const CombinedEquityChart = ({ bots, curves }) => {
  const allPoints = useMemo(() => {
    const tsSet = new Set();
    Object.values(curves).forEach((arr) => arr.forEach((p) => tsSet.add(p.ts)));
    const tss = Array.from(tsSet).sort();
    return tss.map((ts) => {
      const row = { ts, label: new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) };
      let total = 0;
      bots.forEach((b) => {
        const point = (curves[b.bot_id] || []).find((p) => p.ts === ts);
        if (point) {
          row[b.bot_id] = point.equity_usd;
          total += parseFloat(point.equity_usd) || 0;
        }
      });
      row.total = total > 0 ? total : null;
      return row;
    });
  }, [bots, curves]);

  if (allPoints.length < 2) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader><CardTitle className="text-zinc-100 text-base">Equity combinada (48h)</CardTitle></CardHeader>
        <CardContent><p className="text-center text-zinc-500 py-6 text-sm">
          Aún no hay suficientes puntos. Espera al menos 2 heartbeats por bot.
        </p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="combined-equity-chart">
      <CardHeader>
        <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-violet-400" /> Equity combinada (48h)
        </CardTitle>
        <CardDescription>{allPoints.length} puntos · suma de los {bots.length} bots</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={allPoints}>
            <defs>
              <linearGradient id="eq-total" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#a855f7" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 10 }} />
            <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={(v) => formatUSDCompact(v)} />
            <Tooltip
              contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }}
              formatter={(v, name) => [formatUSD(v), name === 'total' ? 'Total' : BOT_LABELS[name] || name]}
            />
            <Area type="monotone" dataKey="total" stroke="#a855f7" strokeWidth={2} fill="url(#eq-total)" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Markdown render para AI Report (compacto)
const ReportMarkdown = ({ md }) => {
  if (!md) return null;
  const lines = md.split('\n');
  const blocks = [];
  let key = 0;
  let listBuf = null;
  let tableBuf = null;
  const flushList = () => {
    if (listBuf && listBuf.length) {
      blocks.push(<ul key={`l${key++}`} className="list-disc pl-6 my-3 space-y-1 text-zinc-300">
        {listBuf.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ul>);
      listBuf = null;
    }
  };
  const flushTable = () => {
    if (tableBuf && tableBuf.length >= 2) {
      const headers = tableBuf[0].split('|').filter((c) => c.trim()).map((c) => c.trim());
      const rows = tableBuf.slice(2).map((r) => r.split('|').filter((c) => c.trim()).map((c) => c.trim()));
      blocks.push(<div key={`t${key++}`} className="my-4 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="border-b border-zinc-700">{headers.map((h, i) => <th key={i} className="text-left p-2 font-semibold text-zinc-200">{renderInline(h)}</th>)}</tr></thead>
          <tbody>{rows.map((r, ri) => <tr key={ri} className="border-b border-zinc-800 hover:bg-zinc-800/30">{r.map((c, ci) => <td key={ci} className="p-2 text-zinc-300">{renderInline(c)}</td>)}</tr>)}</tbody>
        </table>
      </div>);
    }
    tableBuf = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (t.startsWith('### ')) { flushList(); flushTable(); blocks.push(<h3 key={`h${key++}`} className="text-base font-bold text-zinc-100 mt-5 mb-2">{renderInline(t.slice(4))}</h3>); continue; }
    if (t.startsWith('## ')) { flushList(); flushTable(); blocks.push(<h2 key={`h${key++}`} className="text-lg font-bold text-violet-300 mt-6 mb-3 pb-1 border-b border-zinc-800">{renderInline(t.slice(3))}</h2>); continue; }
    if (t.startsWith('# ')) { flushList(); flushTable(); blocks.push(<h1 key={`h${key++}`} className="text-2xl font-bold text-zinc-100 mt-2 mb-4">{renderInline(t.slice(2))}</h1>); continue; }
    if (t === '---') { flushList(); flushTable(); blocks.push(<hr key={`hr${key++}`} className="my-4 border-zinc-800" />); continue; }
    if (t.startsWith('|') && t.endsWith('|')) { flushList(); tableBuf = tableBuf || []; tableBuf.push(t); continue; }
    if (tableBuf) flushTable();
    if (/^[-*]\s/.test(t)) { flushTable(); listBuf = listBuf || []; listBuf.push(t.slice(2)); continue; }
    if (/^\d+\.\s/.test(t)) { flushTable(); listBuf = listBuf || []; listBuf.push(t.replace(/^\d+\.\s/, '')); continue; }
    flushList();
    if (t === '') continue;
    blocks.push(<p key={`p${key++}`} className="my-2 text-zinc-300 leading-relaxed">{renderInline(t)}</p>);
  }
  flushList(); flushTable();
  return <>{blocks}</>;
};

const renderInline = (text) => {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let idx = 0;
  while (remaining.length > 0) {
    const mBold = remaining.match(/\*\*([^*]+)\*\*/);
    const mCode = remaining.match(/`([^`]+)`/);
    let first = null;
    if (mBold && (!mCode || mBold.index <= mCode.index)) first = { type: 'bold', m: mBold };
    else if (mCode) first = { type: 'code', m: mCode };
    if (!first) { parts.push(<span key={idx++}>{remaining}</span>); break; }
    const before = remaining.slice(0, first.m.index);
    if (before) parts.push(<span key={idx++}>{before}</span>);
    if (first.type === 'bold') parts.push(<strong key={idx++} className="text-zinc-100 font-semibold">{first.m[1]}</strong>);
    else parts.push(<code key={idx++} className="text-violet-300 bg-zinc-800/60 px-1 py-0.5 rounded text-xs font-mono">{first.m[1]}</code>);
    remaining = remaining.slice(first.m.index + first.m[0].length);
  }
  return <>{parts}</>;
};

// =============================================================================
// Main Page
// =============================================================================
export const AdminOps = () => {
  const { user, loading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [equityCurves, setEquityCurves] = useState({});  // 48h heartbeats
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ bot: 'all', level: 'all' });
  const [alerts, setAlerts] = useState([]);
  const [aiReport, setAiReport] = useState(null);
  const [aiHistory, setAiHistory] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [killHalted, setKillHalted] = useState(0);        // nº de switches activos (badge)
  const [killGlobal, setKillGlobal] = useState(false);    // parada global activa

  // Per-bot analytics state (timeframe + filtros + datos)
  const [botDays, setBotDays] = useState({});           // bot_id -> days
  const [botStrategy, setBotStrategy] = useState({});   // bot_id -> strategy filter
  const [botMarket, setBotMarket] = useState({});       // bot_id -> market filter
  const [botAnalytics, setBotAnalytics] = useState({}); // bot_id -> analytics payload
  const [botTrades, setBotTrades] = useState({});       // bot_id -> trades raw
  const [botLoading, setBotLoading] = useState({});     // bot_id -> bool

  // Comparison state
  const [compareDays, setCompareDays] = useState(30);
  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  const isAdmin = useMemo(() => {
    return user && ADMIN_EMAILS.includes((user.email || '').toLowerCase());
  }, [user]);

  // === Carga genérica ===
  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const ov = await apiGet('/api/ops/overview');
      setOverview(ov);

      // Equity curves heartbeats (48h) para chart combinada
      const curves = {};
      for (const b of (ov.bots || [])) {
        try {
          const c = await apiGet(`/api/ops/bots/${b.bot_id}/equity-curve?hours=48`);
          curves[b.bot_id] = c.points || [];
        } catch (_) { curves[b.bot_id] = []; }
      }
      setEquityCurves(curves);

      // Logs globales
      const allLogs = [];
      for (const b of (ov.bots || [])) {
        try {
          const l = await apiGet(`/api/ops/bots/${b.bot_id}/logs?limit=50`);
          (l.logs || []).forEach((entry) => allLogs.push({ ...entry, _bot: b.bot_id }));
        } catch (_) {}
      }
      allLogs.sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
      setLogs(allLogs.slice(0, 200));

      // Alertas
      const al = await apiGet('/api/ops/alerts?only_open=true&limit=50');
      setAlerts(al.alerts || []);

      // Parada de emergencia (estado para el badge del tab)
      try {
        const ks = await apiGet('/api/ops/killswitch');
        const halted = (ks.global?.enabled ? 1 : 0) +
          Object.values(ks.bots || {}).filter((b) => b.enabled).length;
        setKillHalted(halted);
        setKillGlobal(!!ks.global?.enabled);
      } catch (_) { /* no romper el dashboard si falla */ }

      // AI Reports
      try {
        const latest = await apiGet('/api/ops/ai/latest-report');
        setAiReport(latest.report || null);
      } catch (_) { setAiReport(null); }
      try {
        const hist = await apiGet('/api/ops/ai/reports?limit=15');
        setAiHistory(hist.reports || []);
      } catch (_) { setAiHistory([]); }

      setLastRefresh(new Date());
    } catch (e) {
      toast.error(`Error cargando ops: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // === Carga analytics por bot ===
  const fetchBotAnalytics = useCallback(async (botId) => {
    const days = botDays[botId] ?? 30;
    const strategy = botStrategy[botId] || '';
    const market = botMarket[botId] || '';
    setBotLoading((s) => ({ ...s, [botId]: true }));
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (strategy) params.set('strategy', strategy);
      if (market) params.set('market', market);
      const a = await apiGet(`/api/ops/bots/${botId}/analytics?${params}`);
      setBotAnalytics((s) => ({ ...s, [botId]: a }));
      // Trades (con mismo timeframe, pero hasta 500 para la tabla)
      const t = await apiGet(`/api/ops/bots/${botId}/trades?limit=500&days=${days}`);
      // Aplicar filtros client-side a la tabla
      let rows = t.trades || [];
      if (strategy) rows = rows.filter((r) => r.strategy === strategy);
      if (market) rows = rows.filter((r) => r.market === market);
      setBotTrades((s) => ({ ...s, [botId]: rows }));
    } catch (e) {
      toast.error(`Error analytics ${botId}: ${e.message}`);
    } finally {
      setBotLoading((s) => ({ ...s, [botId]: false }));
    }
  }, [botDays, botStrategy, botMarket]);

  // === Carga comparación ===
  const fetchComparison = useCallback(async () => {
    setComparisonLoading(true);
    try {
      const c = await apiGet(`/api/ops/compare?days=${compareDays}`);
      setComparison(c);
    } catch (e) {
      toast.error(`Error comparación: ${e.message}`);
    } finally {
      setComparisonLoading(false);
    }
  }, [compareDays]);

  // Bootstrap
  useEffect(() => { if (isAdmin) fetchAll(); }, [isAdmin, fetchAll]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh || !isAdmin) return;
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, isAdmin, fetchAll]);

  // Carga analytics cuando se cambia de tab a un bot, o sus filtros/timeframe
  useEffect(() => {
    const tab = activeTab;
    if (!overview || !tab) return;
    const isBotTab = (overview.bots || []).some((b) => b.bot_id === tab);
    if (isBotTab) fetchBotAnalytics(tab);
  }, [activeTab, fetchBotAnalytics, overview]);

  // Carga comparación cuando se entra al tab o cambia el timeframe
  useEffect(() => {
    if (activeTab === 'compare' && isAdmin) fetchComparison();
  }, [activeTab, compareDays, isAdmin, fetchComparison]);

  const setDaysFor = (botId) => (days) => setBotDays((s) => ({ ...s, [botId]: days }));
  const setStrategyFor = (botId) => (strategy) => setBotStrategy((s) => ({ ...s, [botId]: strategy }));
  const setMarketFor = (botId) => (market) => setBotMarket((s) => ({ ...s, [botId]: market }));

  const resolveAlert = async (alertId) => {
    try {
      const r = await fetch(`${API_URL}/api/ops/alerts/${alertId}/resolve`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Alerta resuelta');
      fetchAll();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  };

  const generateReport = async (sendTelegram = false) => {
    setGeneratingReport(true);
    toast.info('Generando informe con Claude Sonnet 4.5... (~40s)');
    try {
      const r = await fetch(
        `${API_URL}/api/ops/ai/generate-daily?send_telegram=${sendTelegram}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      const d = await r.json();
      setAiReport(d.report);
      toast.success(`Informe generado${sendTelegram ? ' + enviado a Telegram' : ''}`);
      fetchAll();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) return <div className="p-8 text-zinc-400">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <div className="p-8 text-rose-400">Acceso denegado. Solo admin.</div>;

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="admin-ops-page">
      <AdvisorChat />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-zinc-100 flex items-center gap-2">
            <Cpu className="w-7 h-7 text-violet-400" />
            Invictus Ops Center
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Cerebro central de todos los bots · {lastRefresh ? `Actualizado ${formatTs(lastRefresh)}` : 'Cargando...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="ops-toggle-autorefresh-btn"
            variant="outline" size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            className="border-zinc-800 hover:border-violet-500/50"
          >
            {autoRefresh ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-400" /> : <XCircle className="w-4 h-4 mr-1 text-zinc-500" />}
            Auto-refresh 30s
          </Button>
          <Button
            data-testid="ops-refresh-btn"
            size="sm" onClick={fetchAll} disabled={refreshing}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>
      </div>

      {/* KPIs globales */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard testId="kpi-total-equity" icon={TrendingUp} label="Equity total" value={formatUSD(overview.total_equity_usd)} />
          <KpiCard testId="kpi-pnl-today" icon={(overview.total_pnl_today_usd || 0) >= 0 ? ArrowUpRight : ArrowDownRight}
            label="PnL hoy" value={formatUSD(overview.total_pnl_today_usd)}
            tone={(overview.total_pnl_today_usd || 0) >= 0 ? 'success' : 'danger'} />
          <KpiCard testId="kpi-pnl-7d" label="PnL 7d" value={formatUSD(overview.total_pnl_7d_usd)}
            tone={(overview.total_pnl_7d_usd || 0) >= 0 ? 'success' : 'danger'} />
          <KpiCard testId="kpi-bots-active" icon={Bot} label="Bots activos"
            value={`${overview.bots_active}/${overview.bots_total}`}
            tone={overview.bots_active === overview.bots_total ? 'success' : 'warning'} />
          <KpiCard testId="kpi-positions" icon={Activity} label="Posiciones" value={overview.open_positions} />
          <KpiCard testId="kpi-alerts" icon={AlertTriangle} label="Alertas abiertas" value={overview.alerts_open}
            tone={overview.alerts_open > 0 ? 'warning' : 'default'} />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900/60 border border-zinc-800 flex-wrap">
          <TabsTrigger data-testid="ops-tab-overview" value="overview">Resumen</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-fleet" value="fleet">
            <Database className="w-4 h-4 mr-1 text-violet-300" /> Flota
          </TabsTrigger>
          {(overview?.bots || []).map((b) => (
            <TabsTrigger data-testid={`ops-tab-${b.bot_id}`} key={b.bot_id} value={b.bot_id}>
              {BOT_LABELS[b.bot_id] || b.bot_id}
            </TabsTrigger>
          ))}
          <TabsTrigger data-testid="ops-tab-compare" value="compare">
            <BarChart3 className="w-4 h-4 mr-1 text-violet-300" /> Comparar
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-logs" value="logs">
            Logs <Badge className="ml-2 bg-zinc-800">{logs.length}</Badge>
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-alerts" value="alerts">
            Alertas {alerts.length > 0 && <Badge className="ml-2 bg-amber-600">{alerts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-ai" value="ai">
            <Sparkles className="w-4 h-4 mr-1 text-violet-400" /> AI Report
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-hlcontrol" value="hlcontrol">Control HL</TabsTrigger>
          <TabsTrigger data-testid="ops-tab-sentiment" value="sentiment">
            <Brain className="w-4 h-4 mr-1 text-orange-400" /> Sentiment
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-smart-exit" value="smart-exit">
            <Scissors className="w-4 h-4 mr-1 text-sky-400" /> Smart Exit
          </TabsTrigger>
          <TabsTrigger data-testid="ops-tab-config" value="config">
            <Cpu className="w-4 h-4 mr-1 text-violet-400" /> Config
          </TabsTrigger>
          <TabsTrigger
            data-testid="ops-tab-emergency"
            value="emergency"
            className={killHalted > 0 ? 'data-[state=inactive]:text-rose-300' : ''}
          >
            <OctagonX className={`w-4 h-4 mr-1 ${killHalted > 0 ? 'text-rose-400' : 'text-rose-300/70'}`} />
            Emergencia
            {killHalted > 0 && (
              <Badge className="ml-2 bg-rose-600" data-testid="emergency-tab-badge">{killGlobal ? 'GLOBAL' : killHalted}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <CombinedEquityChart bots={overview?.bots || []} curves={equityCurves} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(overview?.bots || []).map((b) => (
              <BotStatusCard key={b.bot_id} bot={b} onClick={() => setActiveTab(b.bot_id)} onReset={fetchAll} />
            ))}
            {(overview?.bots || []).length === 0 && (
              <Card className="bg-zinc-900/60 border-zinc-800 col-span-full">
                <CardContent className="p-8 text-center text-zinc-500">
                  Sin heartbeats todavía. Esperando que los bots envíen datos…
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* FLOTA — Catálogo de despliegues */}
        <TabsContent value="fleet" className="space-y-4">
          <FleetPanel />
        </TabsContent>

        {/* SENTIMENT — edge por régimen + propuesta de pesos (solo lectura) */}
        <TabsContent value="sentiment" className="space-y-4">
          <SentimentInsights />
        </TabsContent>

        {/* SMART EXIT — motor de salidas inteligentes (Fase 1 SHADOW) */}
        <TabsContent value="smart-exit" className="space-y-4">
          <SmartExitPanel />
        </TabsContent>

        {/* PER BOT — Análisis/Trades arriba + Lab de Señales debajo (apilado) */}
        {(overview?.bots || []).map((b) => (
          <TabsContent key={b.bot_id} value={b.bot_id} className="space-y-6">
            <BotAnalytics
              bot={b}
              analytics={botAnalytics[b.bot_id]}
              trades={botTrades[b.bot_id] || []}
              days={botDays[b.bot_id] ?? 30}
              onDaysChange={setDaysFor(b.bot_id)}
              strategy={botStrategy[b.bot_id]}
              onStrategyChange={setStrategyFor(b.bot_id)}
              market={botMarket[b.bot_id]}
              onMarketChange={setMarketFor(b.bot_id)}
              loading={botLoading[b.bot_id]}
            />
            <div className="pt-4 border-t border-zinc-800">
              <BotSignals botId={b.bot_id} />
            </div>
          </TabsContent>
        ))}

        {/* COMPARACIÓN */}
        <TabsContent value="compare" className="space-y-4">
          <BotComparison
            days={compareDays}
            onDaysChange={setCompareDays}
            comparison={comparison}
            loading={comparisonLoading}
          />
        </TabsContent>


        {/* ALERTAS */}
        <TabsContent value="alerts">
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" /> Alertas abiertas
              </CardTitle>
              <CardDescription>Necesitan revisión</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-center text-emerald-400 py-8">
                  <CheckCircle2 className="w-8 h-8 inline mr-2" /> Sin alertas abiertas
                </p>
              ) : (
                <div className="space-y-3">
                  {alerts.map((a, i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      a.severity === 'critical' ? 'bg-rose-950/30 border-rose-800' :
                      a.severity === 'warning' ? 'bg-amber-950/30 border-amber-800' :
                      'bg-zinc-900 border-zinc-800'
                    }`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{a.bot_id}</Badge>
                            <Badge className={`text-xs ${
                              a.severity === 'critical' ? 'bg-rose-600' :
                              a.severity === 'warning' ? 'bg-amber-600' : 'bg-zinc-700'
                            }`}>{a.severity}</Badge>
                            <span className="text-xs text-zinc-500">{a.alert_type}</span>
                            <span className="text-xs text-zinc-500">· {formatTs(a.ts)}</span>
                          </div>
                          <p className="mt-2 font-semibold text-zinc-100">{a.title}</p>
                          {a.details && (
                            <pre className="mt-2 text-xs text-zinc-500 font-mono bg-zinc-950/50 p-2 rounded">
                              {JSON.stringify(a.details, null, 2)}
                            </pre>
                          )}
                        </div>
                        <Button data-testid={`resolve-alert-${a._id || i}-btn`} size="sm" variant="outline"
                          onClick={() => resolveAlert(a._id)}
                          className="border-zinc-700 hover:border-emerald-500/50">
                          Resolver
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI REPORT */}
        <TabsContent value="ai" className="space-y-4">
          <Card className="bg-gradient-to-br from-violet-950/30 to-zinc-900/60 border-violet-900/40">
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-zinc-100 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-400" /> AI Daily Report
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Análisis automático generado por Claude Sonnet 4.5
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="ai-generate-no-telegram-btn"
                    onClick={() => generateReport(false)} disabled={generatingReport}
                    variant="outline" className="border-violet-700 hover:border-violet-500">
                    <Sparkles className={`w-4 h-4 mr-1 ${generatingReport ? 'animate-pulse' : ''}`} />
                    {generatingReport ? 'Generando...' : 'Generar ahora'}
                  </Button>
                  <Button data-testid="ai-generate-with-telegram-btn"
                    onClick={() => generateReport(true)} disabled={generatingReport}
                    className="bg-violet-600 hover:bg-violet-700">
                    <Send className="w-4 h-4 mr-1" /> Generar + Telegram
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {aiReport ? (
                <div>
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <Badge variant="outline" className="text-xs">Modelo: {aiReport.model_used}</Badge>
                    <Badge variant="outline" className="text-xs">
                      <Clock className="w-3 h-3 mr-1" /> {formatTs(aiReport.generated_at)}
                    </Badge>
                    {aiReport.telegram_sent && (
                      <Badge className="text-xs bg-emerald-600">
                        <Send className="w-3 h-3 mr-1" /> Telegram OK
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      Bots analizados: {(aiReport.bots_analyzed || []).join(', ') || '—'}
                    </Badge>
                  </div>
                  <article className="prose prose-invert prose-zinc max-w-none ai-report-md">
                    <ReportMarkdown md={aiReport.summary_md} />
                  </article>
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                  <p>Sin informes generados todavía.</p>
                  <p className="text-xs mt-1">Pulsa "Generar ahora" para crear el primero (~40s).</p>
                </div>
              )}
            </CardContent>
          </Card>

          {aiHistory.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-zinc-100 text-base">Histórico de informes</CardTitle>
                <CardDescription>Últimos {aiHistory.length} generados</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Fecha</TableHead>
                      <TableHead className="text-zinc-400">Bots</TableHead>
                      <TableHead className="text-zinc-400 text-right">PnL 24h</TableHead>
                      <TableHead className="text-zinc-400 text-right">Trades 24h</TableHead>
                      <TableHead className="text-zinc-400">Telegram</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiHistory.map((r, i) => {
                      const pnl = Object.values(r.metrics_snapshot?.pnl_24h_by_bot || {})
                        .reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
                      return (
                        <TableRow key={i} className="border-zinc-800 text-xs">
                          <TableCell className="text-zinc-300">{formatTs(r.generated_at)}</TableCell>
                          <TableCell>
                            {(r.bots_analyzed || []).map((b) => <Badge key={b} variant="outline" className="text-xs mr-1">{b}</Badge>)}
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {pnl >= 0 ? '+' : ''}{formatUSD(pnl)}
                          </TableCell>
                          <TableCell className="text-right text-zinc-300">{r.metrics_snapshot?.trades_24h_count || 0}</TableCell>
                          <TableCell>
                            {r.telegram_sent ? <Badge className="text-xs bg-emerald-700">enviado</Badge> : <span className="text-zinc-500 text-xs">—</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONFIG REMOTA */}
        <TabsContent value="config" className="space-y-4">
          <RiskControlPanel isAdmin={isAdmin} />
          <BotConfigEditor bots={overview?.bots || []} isAdmin={isAdmin} />
        </TabsContent>

        {/* PARADA DE EMERGENCIA */}
        <TabsContent value="emergency" className="space-y-4">
          <EmergencyStopPanel
            bots={overview?.bots || []}
            isAdmin={isAdmin}
            onStateChange={(halted, global) => { setKillHalted(halted); setKillGlobal(global); }}
          />
        </TabsContent>

        {/* LOGS UNIFICADOS */}
        <TabsContent value="logs" className="space-y-4">
          <LogsPanel isAdmin={isAdmin} />
        </TabsContent>
        {/* CONTROL HL */}
        <TabsContent value="hlcontrol" className="space-y-4">
          <HLControlPanel isAdmin={isAdmin} />
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default AdminOps;
